import React from 'react';
import styled from 'styled-components';
import { theme, toRem } from 'design';
import { ITipCalculatorResultIgnoredCheck } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';

interface IIgnoredChecksBreakdownProps {
  ignoredCheckData: ITipCalculatorResultIgnoredCheck[];
}

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  padding: ${toRem(theme.space.lg)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: 8px;
  font-size: ${toRem(theme.font.size.xs)};
  width: 100%;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  width: 100%;
  font-size: ${toRem(theme.font.size.xs)};
  color: ${theme.color.neutral[900]};

  &:not(:last-child) {
    margin-bottom: ${toRem(theme.space.sm)};
  }
`;

const HeaderRow = styled(Row)`
  display: flex;
  flex-flow: column-reverse nowrap;

  &:first-child {
    margin-bottom: ${toRem(theme.space.lg)};
  }

  @media (min-width: 500px) {
    flex-flow: row nowrap;
    align-items: center;
  }
`;

interface StyledTextProps {
  fontColor?: string;
  fontSize?: number;
  fontStyle?: 'italic';
  fontWeight?: 'medium' | 'semiBold';
}

const Text = styled.p<StyledTextProps>`
  color: ${({ fontColor }) => fontColor || theme.color.neutral[900]};
  font-size: ${({ fontSize }) => toRem(fontSize || theme.font.size.xs)};
  font-style: ${({ fontStyle }) => fontStyle || 'normal'};
  font-weight: ${({ fontWeight }) => theme.font.weight[fontWeight] || theme.font.weight.regular};
`;

const CheckListContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(theme.space.sm)};
`;

const IgnoredChecksBreakdown: React.FC<IIgnoredChecksBreakdownProps> = ({ ignoredCheckData }) => {
  if (!ignoredCheckData) return <></>;

  const ignoredCheckTotal = ignoredCheckData.reduce((acc, check) => acc + check.totals.net, 0) || 0;

  const renderCheckList = ignoredCheckData.map((check, idx) => (
    <Row key={`ignored-${check.ticketId}-${idx}`}>
      <Text>#{check.displayId}</Text>
      <Text>{centsToDollarsDisplay(check.totals.net || 0)}</Text>
    </Row>
  ));

  return (
    <Layout data-cy="ignored-checks-breakdown">
      <HeaderRow data-cy="ignored-checks-header">
        <Text fontWeight="semiBold">Ignored Checks</Text>
        <Text fontSize={11} fontStyle="italic">
          These checks are not included in the payout
        </Text>
      </HeaderRow>
      <Row>
        <Text fontColor={theme.color.neutral[500]} fontWeight="medium">
          Check Number
        </Text>
        <Text fontColor={theme.color.neutral[500]} fontWeight="medium">
          Amount
        </Text>
      </Row>
      <CheckListContainer data-cy="ignored-checks-list">{renderCheckList}</CheckListContainer>
      <Row data-cy="ignored-checks-total">
        <Text fontWeight="semiBold">Total</Text>
        <Text fontWeight="semiBold">{centsToDollarsDisplay(ignoredCheckTotal)}</Text>
      </Row>
    </Layout>
  );
};

export default IgnoredChecksBreakdown;
